/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Icon } from '@components/atoms'
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Button,
	TextField,
	IconButton,
	Link,
	Tooltip,
} from '@mui/material'
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import { Bom, BomType } from '@schemas'
import {
	mono,
	boFont,
	boShared,
	boMedia,
	boTableAsCards,
} from '@components/systems/backoffice/backoffice.tokens'
import { Decimal } from '@utils'

interface BOMListProps {
	boms: BomType[]
	isEditMode: boolean
	onUpdateBOM: (updatedBOM: BomType[], deletedBOMs: BomType[]) => void
}

export const BOMList: React.FC<BOMListProps> = ({ boms, isEditMode, onUpdateBOM }) => {
	const [editableBOM, setEditableBOM] = React.useState<BomType[]>(boms)
	const [deletedBOMs, setDeletedBOMs] = React.useState<BomType[]>([])

	React.useEffect(() => {
		setEditableBOM(boms)
		setDeletedBOMs([])
	}, [boms])

	const margin: BomType = React.useMemo(() => {
		const idx = boms.findIndex(
			aBom => aBom.manufacturer === 'Judahs' && aBom.name.toLowerCase() === 'margin',
		)

		if (idx > 0) {
			return boms[idx]
		}
		return new Bom()
	}, [boms])

	const handleEdit = (id: string, field: keyof BomType, value: any) => {
		const updatedBOM = editableBOM.map(item =>
			item.id === id ? { ...item, [field]: value } : item,
		)
		setEditableBOM(updatedBOM)
	}

	const handleAdd = () => {
		const newItem: BomType = {
			id: `new-${Date.now()}`,
			productId: '',
			manufacturer: '',
			name: '',
			jlcpcbNumber: '',
			link: '',
			unitUsd: 0,
			quantity: 1,
			useYn: 1,
			updatedBy: '',
			updatedAt: new Date().toISOString(),
			createdBy: '',
			createdAt: new Date().toISOString(),
		}
		setEditableBOM([...editableBOM, newItem])
	}

	const handleDelete = (id: string) => {
		const deletedBOM = editableBOM.find(item => item.id === id)
		if (deletedBOM) {
			setDeletedBOMs([...deletedBOMs, deletedBOM])
		}
		setEditableBOM(editableBOM.filter(item => item.id !== id))
	}

	const handleSave = () => {
		onUpdateBOM(editableBOM, deletedBOMs)
	}

	const totalCost = editableBOM
		.filter(item => item.useYn === 1)
		.reduce((sum, item) => sum + item.unitUsd * item.quantity, 0)

	return (
		<View css={style}>
			<TableContainer component={Paper} elevation={3} className="bomTableContainer">
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell className="headerCell">제조사</TableCell>
							<TableCell className="headerCell">제품명</TableCell>
							<TableCell className="headerCell">JLCPCB 넘버</TableCell>
							<TableCell className="headerCell">수량</TableCell>
							<TableCell className="headerCell">단가 (USD)</TableCell>
							<TableCell className="headerCell">원재가 (USD)</TableCell>
							<TableCell className="headerCell">링크</TableCell>
							<TableCell className="headerCell useYn">사용여부</TableCell>
							{isEditMode && <TableCell className="headerCell">삭제</TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{editableBOM.map(row => (
							<TableRow key={row.id} className={`tableRow ${isEditMode ? 'edit' : ''}`}>
								<TableCell data-label="제조사">
									{isEditMode ? (
										<TextField
											value={row.manufacturer}
											onChange={e => handleEdit(row.id, 'manufacturer', e.target.value)}
											variant="outlined"
											size="small"
											fullWidth
										/>
									) : (
										row.manufacturer
									)}
								</TableCell>
								<TableCell data-label="제품명">
									{isEditMode ? (
										<TextField
											value={row.name}
											onChange={e => handleEdit(row.id, 'name', e.target.value)}
											variant="outlined"
											size="small"
											fullWidth
										/>
									) : (
										row.name
									)}
								</TableCell>
								<TableCell data-label="JLCPCB">
									{isEditMode ? (
										<TextField
											value={row.jlcpcbNumber}
											onChange={e => handleEdit(row.id, 'jlcpcbNumber', e.target.value)}
											variant="outlined"
											size="small"
											fullWidth
										/>
									) : (
										row.jlcpcbNumber
									)}
								</TableCell>
								<TableCell data-label="수량" css={{ textAlign: 'right' }}>
									{isEditMode ? (
										<TextField
											type="number"
											value={row.quantity}
											onChange={e =>
												handleEdit(row.id, 'quantity', Number(e.target.value))
											}
											variant="outlined"
											size="small"
											inputProps={{ min: 0, step: 1, style: { textAlign: 'right' } }}
										/>
									) : (
										row.quantity
									)}
								</TableCell>
								<TableCell data-label="단가" css={{ textAlign: 'right' }}>
									{isEditMode ? (
										<TextField
											type="number"
											value={row.unitUsd}
											onChange={e =>
												handleEdit(row.id, 'unitUsd', Number(e.target.value))
											}
											variant="outlined"
											size="small"
											inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
										/>
									) : (
										`${row.unitUsd.toLocaleString('en-US', {
											style: 'currency',
											currency: 'USD',
										})}`
									)}
								</TableCell>
								<TableCell data-label="원재가" css={{ textAlign: 'right' }}>
									{`${(row.unitUsd * row.quantity).toLocaleString('en-US', {
										style: 'currency',
										currency: 'USD',
									})}`}
								</TableCell>
								<TableCell data-label="링크">
									{isEditMode ? (
										<TextField
											value={row.link}
											onChange={e => handleEdit(row.id, 'link', e.target.value)}
											variant="outlined"
											size="small"
											fullWidth
										/>
									) : (
										<>
											{row.link && (
												<Link
													href={row.link}
													target="_blank"
													rel="noopener noreferrer"
													className="link"
												>
													링크
												</Link>
											)}
										</>
									)}
								</TableCell>
								<TableCell
									data-label="사용"
									className="useYn"
									style={{ minWidth: isEditMode ? '3rem' : '1rem' }}
								>
									{isEditMode ? (
										<TextField
											select
											value={row.useYn}
											onChange={e => handleEdit(row.id, 'useYn', Number(e.target.value))}
											SelectProps={{ native: true }}
											variant="outlined"
											size="small"
										>
											<option value={1}>Yes</option>
											<option value={0}>No</option>
										</TextField>
									) : (
										<View css={{ textAlign: 'center' }}>
											{row.useYn === 1 && (
												<Icon name="check" css={{ fontSize: 14, color: mono.ink }} />
											)}
										</View>
									)}
								</TableCell>
								{isEditMode && (
									<TableCell data-label="삭제">
										<Tooltip title="Delete">
											<IconButton onClick={() => handleDelete(row.id)} size="small">
												<DeleteIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</TableCell>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			{isEditMode && (
				<Button
					variant="outlined"
					startIcon={<AddIcon />}
					onClick={handleAdd}
					className="addButton"
					size="small"
				>
					BOM 추가
				</Button>
			)}

			<View className="summaryView">
				<View className="summaryDataView">
					<span className="boSpecLabel summaryLabel">제품원가:</span>
					<span className="summaryValue">
						{(totalCost - margin.unitUsd).toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</span>
				</View>

				<View className="summaryDataView">
					<span className="boSpecLabel summaryLabel">영업이익:</span>
					<span className="summaryValue">
						{margin.unitUsd.toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</span>
				</View>

				<View className="summaryDataView">
					<span className="boSpecLabel summaryLabel">VAT:</span>
					<span className="summaryValue">
						{(totalCost / 10).toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</span>
				</View>

				<View className="summaryDataView">
					<span className="boSpecLabel summaryLabel">실판매가:</span>
					<span className="summaryValue">
						{(totalCost + totalCost / 10).toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</span>
				</View>

				<View className="summaryDataView">
					<span className="boSpecLabel summaryLabel">영업이익률:</span>
					<span className="summaryValue">
						{`${Decimal((margin.unitUsd / totalCost) * 100)}%`}
					</span>
				</View>
			</View>

			{isEditMode && (
				<Button variant="contained" onClick={handleSave} className="saveButton">
					저장
				</Button>
			)}
		</View>
	)
}

export default BOMList

const style = css({
	...boShared,
	marginTop: 0,
	display: 'flex',
	flexDirection: 'column',
	gap: 20,
	height: 'fit-content',
	width: '100%',
	overflow: 'hidden',

	'.bomTableContainer': {
		flex: 1,
		overflow: 'auto',
		width: '100%',
		height: 'fit-content',
		boxShadow: 'none',
		background: 'transparent',
		borderRadius: 0,

		// 전역 CSS 의 문서 뷰어 잔재(section table 보더)를 무효화한다 — DESIGN_SYSTEM §10
		'.MuiTable-root': { border: 0, background: 'transparent', textAlign: 'left' },
		'.MuiTableCell-root': { border: 0, fontFamily: boFont.ui },

		'.headerCell': {
			position: 'sticky',
			top: 0,
			zIndex: 1,
			padding: '10px 10px',
			backgroundColor: `${mono.bg} !important`,
			borderBottom: `1px solid ${mono.line}`,
			fontSize: 11,
			fontWeight: 500,
			letterSpacing: '.12em',
			textTransform: 'uppercase',
			color: mono.faint,
			whiteSpace: 'nowrap',
		},

		'.tableRow': {
			'.MuiTableCell-body': {
				padding: '8px 10px',
				fontSize: 13,
				fontWeight: 300,
				color: mono.ink,
				borderBottom: `1px solid ${mono.line}`,
				fontVariantNumeric: 'tabular-nums',

				'.MuiOutlinedInput-root': {
					'.MuiInputBase-input:not(.MuiNativeSelect-select)': {
						padding: '5px 8px',
						fontSize: 12,
					},
					'.MuiNativeSelect-select': {
						paddingTop: 5,
						paddingBottom: 5,
						paddingLeft: 8,
						fontSize: 12,
						lineHeight: 'normal',
					},
				},
			},
			'&:hover': {
				backgroundColor: mono.surface,
			},

			'&.edit': {
				'.MuiTableCell-body': {
					padding: '4px 4px',
				},
			},
		},

		'.link': {
			textDecoration: 'none',
			color: mono.ink,
			borderBottom: `1px solid ${mono.ink}`,

			'&:hover': {
				textDecoration: 'none',
				color: mono.dim,
			},
		},
	},

	'.addButton': {
		alignSelf: 'flex-start',
	},

	'.summaryView': {
		paddingTop: 8,
		borderTop: `1px solid ${mono.line}`,
		alignSelf: 'flex-end',
		width: '22rem',

		'.summaryDataView': {
			display: 'flex',
			alignItems: 'baseline',
			justifyContent: 'space-between',
			width: '100%',
			padding: '10px 0',
			borderBottom: `1px solid ${mono.line}`,

			'.summaryValue': {
				fontSize: 16,
				fontWeight: 300,
				color: mono.ink,
				fontVariantNumeric: 'tabular-nums',
			},
		},
	},

	'.saveButton': {
		alignSelf: 'flex-end',
	},
	// 폰 — 표가 아니라 카드 (2026-09-29 사용자 요청). 컨테이너의 자기 스크롤도 없앤다
	[boMedia.phone]: {
		'.bomTableContainer': { overflow: 'visible', maxWidth: '100%' },
		'.MuiTable-root': boTableAsCards,
		'.MuiTable-root tbody td .MuiTextField-root, .MuiTable-root tbody td .MuiInputBase-root':
			{ minWidth: 0, width: '100%' },
		'.MuiTable-root tbody td.cardTitle': { fontSize: 15, fontWeight: 500 },
	},
})
